import { Context } from "@faire/deprecated-retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Context";
import { EditableAvatar } from "@faire/deprecated-retailer-visitor-shared/components/LayoutElementRenderer/__internal__/MultiUser/EditableAvatar";
import { getTeamMemberProfileImages } from "@faire/deprecated-retailer-visitor-shared/components/LayoutElementRenderer/__internal__/MultiUser/utils";
import { ValueProps } from "@faire/deprecated-retailer-visitor-shared/components/LayoutElementRenderer/__internal__/MultiUser/ValueProps";
import { ProfilePictureUploadModal } from "@faire/deprecated-retailer-visitor-shared/components/Modals/ProfilePictureUploadModal/ProfilePictureUploadModal";
import { TeamMembersModal } from "@faire/deprecated-retailer-visitor-shared/components/Modals/TeamMembersModal/TeamMembersModal";
import { TeamMemberUserInfo } from "@faire/deprecated-retailer-visitor-shared/components/TeamMemberUserInfo";
import { getUser } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getUser";
import { useRecordMilestoneByDay } from "@faire/deprecated-retailer-visitor-shared/stores/domain/useMilestone";
import { useUserStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/useUserStore";
import { Core, Decorative } from "@faire/design-tokens";
import { trackUpmarketMultiUserEducationBannerEditableAvatarClick } from "@faire/web-api--source/events/upmarket/click/multiUserEducationBannerEditableAvatar";
import { IGridTile } from "@faire/web-api--source/indigofair/data/IGridTile";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { ISimpleImageTitle } from "@faire/web-api--source/indigofair/data/ISimpleImageTitle";
import { RETAILER_MULTI_USER_TEAM_EDUCATION_BANNER_SEEN } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/RETAILER_MULTI_USER_TEAM_EDUCATION_BANNER_SEEN";
import { Avatar } from "@faire/web/slate-deprecated/Avatar";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import compact from "lodash/compact";
import React from "react";
import styled from "styled-components";

export const MultiUserTeamPermissionEducationBanner: React.FC = () => {
  const { layoutElement } = React.useContext(Context);

  const [showProfilePicUploadModal, setShowProfilePicUploadModal] =
    React.useState(false);

  const [showTeamMembersModal, setShowTeamMembersModal] = React.useState(false);

  const { isMobile } = useViewport();

  const simpleImageTitles = compact(
    layoutElement?.layout_element_data?.map(
      (layoutElementData) => layoutElementData.simple_image_title
    )
  );

  const additionalTeamMembersData = simpleImageTitles.filter(
    (simpleImageTitle) =>
      !simpleImageTitle?.image && !simpleImageTitle?.images.length
  )[0];

  const teamMemberUsersData: ISimpleImageTitle[] = simpleImageTitles.filter(
    (simpleImageTitle) =>
      simpleImageTitle?.image || simpleImageTitle?.images.length
  );

  const valueProps: IGridTile[] = compact(
    layoutElement?.layout_element_data?.map(
      (layoutElementData) => layoutElementData.grid_tile
    )
  );

  const { user, firstName, lastName } = useUserStore();
  const userName = firstName + " " + lastName;

  const recordMilestoneByDay = useRecordMilestoneByDay();

  const ref = useDoActionWhenVisible(() => {
    recordMilestoneByDay(RETAILER_MULTI_USER_TEAM_EDUCATION_BANNER_SEEN);
  });

  const handleUserAvatarClick = () => {
    trackUpmarketMultiUserEducationBannerEditableAvatarClick();
    setShowProfilePicUploadModal(true);
  };

  const handleUploadProfileImageModalClose = () => {
    setShowProfilePicUploadModal(false);
  };

  const userToken = getUser()?.token ?? "";

  return (
    <>
      {showProfilePicUploadModal ? (
        <ProfilePictureUploadModal
          onClose={handleUploadProfileImageModalClose}
        />
      ) : null}
      {showTeamMembersModal ? (
        <TeamMembersModal onClose={() => setShowTeamMembersModal(false)} />
      ) : null}
      <Container
        $backgroundColor={
          layoutElement.background_color ?? Decorative.tint.yellow
        }
        ref={ref}
      >
        <Wrapper>
          <Content>
            <ResponsiveContentV2
              mobile={
                <>
                  <TeamMemberUserInfoRowMobile
                    data-test-id="mobile-team-members-row"
                    onClick={() => setShowTeamMembersModal(true)}
                  >
                    {teamMemberUsersData
                      .flatMap(getTeamMemberProfileImages)
                      .map((image, index) => (
                        <AvatarWrapper
                          data-test-id={`homepage-team-member-avatar-${index}`}
                          key={index}
                        >
                          <TeamMemberAvatar
                            size="x-small"
                            image={IImage.build({
                              url: image.url ?? "",
                            })}
                          />
                        </AvatarWrapper>
                      ))}
                    {additionalTeamMembersData ? (
                      <AdditionalTeamMemberInfo>
                        <Typography
                          variant="labelSansRegular"
                          color={Core.text.inverse}
                        >
                          {additionalTeamMembersData.main_text}
                        </Typography>
                      </AdditionalTeamMemberInfo>
                    ) : null}
                  </TeamMemberUserInfoRowMobile>
                  <Spacer height="1x" />
                </>
              }
            />
            <Typography
              variant={
                isMobile ? "pageHeaderSerifRegular" : "displayMSerifRegular"
              }
            >
              {layoutElement.main_text}
            </Typography>
            <Spacer height="1x" />
            <ValueProps valueProps={valueProps} />
          </Content>
          <Content>
            <ResponsiveContentV2
              tabletAndAbove={
                <TeamMemberUserInfoRow>
                  {[...teamMemberUsersData]
                    .reverse()
                    .map((teamMemberUserData, index) => {
                      return userToken === teamMemberUserData.token ? (
                        <EditableAvatar
                          key={index}
                          name={userName}
                          onClick={handleUserAvatarClick}
                          image={user?.profile_image}
                        />
                      ) : (
                        <StyledTeamMemberUserInfo
                          key={index}
                          images={getTeamMemberProfileImages(
                            teamMemberUserData
                          )}
                          subtitle={teamMemberUserData.main_text ?? ""}
                          size="x-large"
                          maxTextWidth={140}
                          spacingAboveTitle="3x"
                          avatarPileOffset={80}
                          onClick={() => setShowTeamMembersModal(true)}
                        />
                      );
                    })}
                </TeamMemberUserInfoRow>
              }
            />
          </Content>
        </Wrapper>
      </Container>
    </>
  );
};

const Container = styled.div<{ $backgroundColor: string }>`
  background-color: ${({ $backgroundColor }) => $backgroundColor};
`;

const Wrapper = styled(Row).attrs({ align: "center" })`
  max-width: 1280px;
  margin: auto;
`;

const TeamMemberUserInfoRow = styled(Row)`
  width: fit-content;
  align-self: center;
`;

const TeamMemberUserInfoRowMobile = styled(Row)`
  width: fit-content;
  align-self: baseline;
  &:hover {
    cursor: pointer;
  }
`;

const AvatarWrapper = styled.div`
  margin-left: -8px;

  &:first-child {
    margin-left: 0;
  }
`;

const StyledTeamMemberUserInfo = styled(TeamMemberUserInfo)`
  padding-left: ${getSpacing("2x")};
  padding-right: ${getSpacing("2x")};
`;

const Content = styled(Column)`
  padding: ${getSpacing("4x", "3x")};

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing("6x", "7x")};
    width: 50%;
  }
`;

const TeamMemberAvatar = styled(Avatar)`
  border: 1px solid ${Core.surface.subdued};
`;

const AdditionalTeamMemberInfo = styled(Column).attrs({
  justify: "center",
  align: "center",
})`
  border: 1px solid ${Core.surface.subdued};
  background-color: ${Core.surface.default};
  border-radius: 50%;
  width: 32px;
  height: 32px;
  margin-left: -8px;
  z-index: 2;
`;
